import React from 'react';
import { v4 } from 'uuid';

import { CategoryStateContext } from '~src/category/category-state';
import { SessionStateContext } from '~src/global-state';
import { useIpc } from '~src/utils/ipc-utils';
import { breadcrumb } from '~src/utils/sentry';

import {
  inlinePostalCodeInputID,
  inlinePostalCodeSubmitButtonID,
} from '~address-form/utils/address-utils';

export type FullscreenComponent<P = {}> = React.FC<P> & { fullscreenName: string };

/*
 * Count the number of fullscreen components that are open. In normal operation
 * there will be just one fullscreen component, but in edge case, which is
 * difficult to prevent, there could be more.
 */
let fullscreenRefCount = 0;

interface FullscreenElementProps {
  componentName: string;
  allProps: Record<string, any>;
}

export const FullscreenElement: React.FC<FullscreenElementProps> = ({
  componentName,
  allProps,
}) => {
  /*
   * Provide unique id to this component to filter ipc messages.
   */
  const [renderId] = React.useState<string>(() => v4());

  const activeElementRef = React.useRef<HTMLElement>();

  const saveLastFocusedElement = () => {
    activeElementRef.current = document.activeElement as HTMLElement;
  };

  const focusLastFocusedElement = () => {
    if (
      activeElementRef.current &&
      // We don't want to focus the inline input fields after error modals are closed
      // This is because everything is handled on the modal level
      activeElementRef.current.id !== inlinePostalCodeInputID &&
      activeElementRef.current.id !== inlinePostalCodeSubmitButtonID
    ) {
      activeElementRef.current.focus();
    }
  };

  const session = React.useContext(SessionStateContext);
  const category = React.useContext(CategoryStateContext);
  const ipc = useIpc();

  // filter out callbacks
  const callbackPropsList: string[] = [];
  const staticProps = Object.assign({}, allProps);

  Object.keys(allProps).forEach(propName => {
    if (typeof staticProps[propName] === 'function') {
      delete staticProps[propName];
      callbackPropsList.push(propName);
    }
  });

  React.useEffect(() => {
    breadcrumb('open fullscreen');

    if (fullscreenRefCount === 0) {
      ipc.send({
        name: 'fullscreen_modal_show',
      });
    }

    saveLastFocusedElement();

    fullscreenRefCount++;

    return () => {
      breadcrumb('close fullscreen');

      ipc.send({
        name: 'hide_fullscreen_component',
        data: { renderId },
      });

      fullscreenRefCount--;

      if (fullscreenRefCount === 0) {
        ipc.send({
          name: 'fullscreen_modal_hide',
        });
      }

      focusLastFocusedElement();
    };
  }, []);

  React.useEffect(() => {
    return ipc.listen(payload => {
      if (payload.name === 'run_fullscreen_callback') {
        if (payload.data.renderId !== renderId) {
          return;
        }
        const propName = payload.data.propName as string;
        const callback = allProps[propName];
        if (!callback) {
          throw `Callback with name "${propName}" not defined`;
        }
        callback(...payload.data.args);
      }
    });
  });

  React.useEffect(
    () => {
      ipc.send({
        name: 'render_fullscreen_component',
        data: {
          renderId,
          componentName,
          staticProps,
          callbackPropsList,
          categoryId: category?.id,
          sessionId: session?.session_id,
        },
      });
    } /* synchronize every change of props with fullscreen. */
  );

  return null;
};

interface FullscreenModeProps {
  children: React.ReactElement;
}
/**
 * Extract the name of child component and pass it to fullscreen iframe for
 * rendering. We do no actual rendering of children in this component, the fact
 * that we pass real child component here serves only for robust typechecking.
 */
export const FullscreenMode: React.FC<FullscreenModeProps> = ({ children }) => {
  const child = React.Children.only(children);

  if (typeof child.type === 'string') {
    throw 'Rendering of ReactText component is of little use in fullscreen';
  }

  // @ts-ignore
  const componentName = child.type.fullscreenName;

  if (typeof componentName !== 'string') {
    throw 'The static prop "fullscreenName" is missing in fullscreen component';
  }

  if (child.props.children) {
    /**
     * Note: we could actually implement this but it seems like this is not
     * necessary.
     */
    throw 'Deep component trees cannot be renderer in fullscreen mode.';
  }

  return <FullscreenElement componentName={componentName} allProps={child.props} />;
};
